"use client";

import React, { createContext, useCallback, useContext, useEffect, useReducer } from "react";

type FormDataState<T> = T;

type FormDataAction<T> =
  | { type: "SET_FORM_DATA"; payload: { data: T } }
  | { type: "REPLACE_FORM_DATA"; payload: { data: T } }
  | { type: "RESET_FORM_DATA" };

type FormDataContextType<T, D> = {
  formData: FormDataState<T>;
  setFormData: (data: D) => void;
  replaceFormData: (data: D) => void;
  resetFormData: () => void;
};

const FormDataContext = createContext<FormDataContextType<any, any> | null>(null);

const initialState = {};

const reducer = <T,>(state: FormDataState<T>, action: FormDataAction<T>) => {
  switch (action.type) {
    case "SET_FORM_DATA":
      return { ...state, ...action.payload.data };
    case "REPLACE_FORM_DATA":
      return action.payload.data;
    case "RESET_FORM_DATA":
      return {};
    default:
      return state;
  }
};

export const FormProvider = ({ children }: { children: React.ReactNode }) => {
  const [formData, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const sessionData = sessionStorage.getItem("formData");
      if (sessionData) {
        dispatch({ type: "SET_FORM_DATA", payload: { data: JSON.parse(sessionData) } });
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("formData", JSON.stringify(formData));
    }
  }, [formData]);

  const setFormData = useCallback(
    (data: any) => dispatch({ type: "SET_FORM_DATA", payload: { data } }),
    []
  );

  const replaceFormData = useCallback(
    (data: any) => dispatch({ type: "REPLACE_FORM_DATA", payload: { data } }),
    []
  );

  const resetFormData = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("formData");
    }
    dispatch({ type: "RESET_FORM_DATA" });
  }, []);

  return (
    <FormDataContext.Provider value={{ formData, setFormData, replaceFormData, resetFormData }}>
      {children}
    </FormDataContext.Provider>
  );
};

export const useFormData = <TState, TData>() => {
  const context = useContext(FormDataContext);
  if (!context) throw new Error('useFormData must be used within a FormDataProvider"');
  return context as FormDataContextType<TState, TData>;
};
